import { useQueries } from "@tanstack/react-query";
import { DateTimeFormatter, LocalDate } from "@js-joda/core";
import React from "react";
import { Page } from "../../../activities/AppPage";
import { API } from "../../../API";
import { isLoading, Loading } from "../../../Loading";
import { Paystub } from "../../../messages/Paystub";
import { STRINGS } from "../../../strings";
import { AppCaregiver } from "../../../system/AppState/AppCaregiverState";
import { translate } from "../../../translate";
import {
  changeSchedule,
  decreaseScheduleByMonth,
  increaseScheduleByMonth,
} from "../../../utils/handleScheduleDates";
import ErrorScaffold from "../../ErrorScaffold";
import LoadingScaffold from "../../LoadingScaffold";
import PaystubListScreen from "./PaystubListScreen";

const queryDateFormatter = DateTimeFormatter.ofPattern("yyyy-MM-dd");

type Props = {
  api: API;
  caregiver: AppCaregiver | Loading;
  onClickGoBack: () => void;
  onRequestNavigate: (page: Page) => void;
};

const PaystubListActivity = (props: Props) => {
  const [scheduleYear, setScheduleYear] = React.useState(LocalDate.now().year());
  const [scheduleMonth, setScheduleMonth] = React.useState(LocalDate.now().month());

  const handleClickPreviousScheduleMonth = React.useCallback(() => {
    return decreaseScheduleByMonth(scheduleMonth, scheduleYear, setScheduleMonth, setScheduleYear);
  }, [scheduleMonth, scheduleYear]);

  const handleClickNextScheduleMonth = React.useCallback(() => {
    return increaseScheduleByMonth(scheduleMonth, scheduleYear, setScheduleMonth, setScheduleYear);
  }, [scheduleMonth, scheduleYear]);

  const handleChangeScheduleMonth = React.useCallback(
    (newYear: number, newMonth: number) => {
      return changeSchedule(
        scheduleMonth,
        scheduleYear,
        newMonth,
        newYear,
        setScheduleMonth,
        setScheduleYear
      );
    },
    [scheduleMonth, scheduleYear]
  );

  const handleClickPaystub = (paystub: Paystub) => {
    props.onRequestNavigate({
      type: "CaregiverPaystubDetails",
      paystub,
    });
  };

  const queryStartDate = LocalDate.of(scheduleYear, scheduleMonth.value(), 1).format(
    queryDateFormatter
  );
  const queryEndDate = LocalDate.of(
    scheduleMonth.value() === 12 ? scheduleYear + 1 : scheduleYear,
    scheduleMonth.plus(1),
    1
  )
    .minusDays(1)
    .format(queryDateFormatter);

  const [paystubsQuery] = useQueries({
    queries: [
      {
        queryKey: ["paystubs", queryStartDate, queryEndDate],
        queryFn: () => props.api.getCaregiverPaystubs(queryStartDate, queryEndDate),
        keepPreviousData: true,
      },
    ],
  });

  if (paystubsQuery.isLoading || isLoading(props.caregiver)) {
    return (
      <LoadingScaffold title={translate(STRINGS.PAYROLL)} onClickGoBack={props.onClickGoBack} />
    );
  }

  if (paystubsQuery.data === undefined || paystubsQuery.isError) {
    return (
      <ErrorScaffold
        title={translate(STRINGS.OOPS)}
        message={translate(STRINGS.SOMETHING_WENT_WRONG)}
        onClickGoBack={props.onClickGoBack}
      />
    );
  }

  return (
    <PaystubListScreen
      paystubs={paystubsQuery.data.paystubs}
      isFetching={paystubsQuery.isFetching}
      onClickGoBack={props.onClickGoBack}
      onClickPaystub={handleClickPaystub}
      scheduleYear={scheduleYear}
      scheduleMonth={scheduleMonth}
      onClickPreviousScheduleMonth={handleClickPreviousScheduleMonth}
      onlickNextScheduleMonth={handleClickNextScheduleMonth}
      onChangeScheduleMonth={handleChangeScheduleMonth}
    />
  );
};

export default PaystubListActivity;
